import { defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'
import isEmpty from 'lodash/isEmpty'
import isPlainObject from 'lodash/isPlainObject'
import moment from 'moment'
import api from '../api'
import { CART_RESERVATION_MS } from '../config'
import { getCardType, generateRandomString } from '../helpers'
import { duplicateSessionCartMessage, getItemTotal, getSeatsUsed } from '../helpers/cart'
import { normaliseConfirmationValues } from '../helpers/confirmations'
import { normaliseParticipantKey } from '../helpers/format'
import { buildSelectedUpsellPayload, buildUpsellRequestItems, resolveUpsellId, upsellMatchesCart } from '../helpers/upsells'

const attractionTemplate = () => ({
  id: uuid(),
  attraction: 0,
  product_code: 0,
  prices: [],
  availability: {},
  extras: [],
})

const cartItemSessionKey = (item = {}) => {
  const productCode = String(item?.product_code ?? item?.productCode ?? '').trim()
  const attractionId = String(item?.attraction ?? item?.attraction_id ?? '').trim()
  const time = item?.time ?? item?.startTimeLocal ?? item?.startTime ?? null
  const productIdentity = productCode
    ? `product:${productCode.toUpperCase()}`
    : attractionId && attractionId !== '0'
      ? `attraction:${attractionId}`
      : ''

  if (!productIdentity || !time) {
    return ''
  }

  const parsedTime = moment(time)
  const normalisedTime = parsedTime.isValid() ? parsedTime.valueOf() : String(time).trim()

  return `${productIdentity}|${normalisedTime}`
}

export const useCheckoutCartStore = defineStore('checkoutCart', {
  state: () => ({
    group: false,
    items: [attractionTemplate()],
    attractions: [],
    bookingNumber: false,
    bookingToken: false,
    options: {
      gateway: false,
      confirm_url: '',
    },
    customer: {
      name: '',
      phone: '',
      email: '',
      lastName: '',
      firstName: '',
    },
    creditCard: {
      cardName: '',
      cardNumber: '',
      expiryYear: '',
      expiryMonth: '',
      cardSecurityNumber: '',
      cardType: '',
      confirmationTokenId: '',
    },
    vouchers: [],
    subscribe: true,
    status: null,
    rcaNumber: '',
    booking: {},
    quote: {},
    answers: [],
    confirms: [],
    preparedPayment: {},
    payment: {
      confirmation: '',
    },
    'g-recaptcha-response': '',
    cartItems: [],
    lastAddedItemId: null,
    cartMessages: [],
    selectedUpsellOffers: [],
    upsellsByPlacement: {
      widget: [],
      cart: [],
    },
    upsellsLoading: {
      widget: false,
      cart: false,
    },
  }),
  getters: {
    form(state) {
      const seenSessions = new Set()
      const items = state.items.reduce((bookingItems, item) => {
        const sessionKey = cartItemSessionKey(item)

        if (sessionKey && seenSessions.has(sessionKey)) {
          return bookingItems
        }

        if (sessionKey) {
          seenSessions.add(sessionKey)
        }

        bookingItems.push({
          id: item.attraction,
          time: item.time,
          participants: item.participants,
          product_code: item.product_code || '',
          extras: (item.extras ?? []).map((extra) => ({
            id: extra.id,
            name: extra.name,
            quantity: extra.quantity,
          })),
        })

        return bookingItems
      }, [])

      let creditCard = {}

      switch (state.options.gateway.type) {
        case 'card':
          creditCard = {
            ...state.creditCard,
            cardType: getCardType(state.creditCard.cardNumber),
          }
          break
        case 'stripe:rezdy':
        case 'stripe:via':
        case 'stripe:rezdy-wallets':
          creditCard = {
            ...state.creditCard,
            cardToken: state.creditCard.cardToken,
          }
          break
        case 'stripe':
          creditCard = {
            ...state.creditCard,
            confirmationTokenId: state.payment.confirmation.id,
          }
          break
        case 'stripe:3d':
          creditCard = {
            ...state.creditCard,
            intent: state.preparedPayment?.intent?.id,
          }
          break
      }

      return {
        items,
        upsells: buildSelectedUpsellPayload(state.selectedUpsellOffers, state.items),
        customer: state.customer,
        creditCard,
        vouchers: state.vouchers,
        rcaNumber: state.rcaNumber,
        subscribe: state.subscribe,
        answers: state.answers,
        confirms: state.confirms,
        'g-recaptcha-response': state['g-recaptcha-response'],
      }
    },
    totalDueCents: (state) => Math.round((state.quote?.totalDue ?? 0) * 100),
    totalPeople: (state) => {
      return state.items.reduce((accumulator, item) => {
        return accumulator + (item.participants
          ? Object.entries(item.participants).reduce((total, [, count]) => total + Number(count || 0), 0)
          : 0)
      }, 0)
    },
    uniqueAttractions(state) {
      if (!Array.isArray(state.items) || !Array.isArray(state.attractions)) {
        return []
      }

      const attractionIds = new Set(
        state.items
          .map((item) => item?.attraction)
          .filter((id) => id !== null && id !== undefined && id !== 0 && id !== '0')
          .map((id) => String(id))
      )

      if (attractionIds.size === 0) {
        return []
      }

      return state.attractions.filter((attraction) => attractionIds.has(String(attraction?.id)))
    },
    cartCount: (state) => state.cartItems.length,
    cartSubtotal: (state) => state.cartItems.reduce((total, item) => total + getItemTotal(item), 0),
    reservationExpiry: () => Date.now() + CART_RESERVATION_MS,
  },
  actions: {
    async ensureLoaded() {
      this.deduplicateCartSessions()
      await this.loadOptions()
      await this.loadAttractions()
    },
    quoteBooking(signal) {
      return api
        .quote(this.form, signal)
        .then((response) => {
          this.setQuote(response)
          return response
        })
        .catch((error) => Promise.reject(error.response || error))
    },
    loadAttractions() {
      if (this.attractions.length > 0) {
        return Promise.resolve(this.attractions)
      }

      return api
        .attractions()
        .then((response) => {
          // axios resolves a request the browser cut off (status 0, empty body) instead of rejecting it,
          // e.g. Firefox when the visitor clicks away mid-load. Treat anything but a list as a failed load.
          if (!Array.isArray(response?.data)) {
            return Promise.reject(new Error(`Unexpected attractions response (status ${response?.status})`))
          }

          this.setAttractions(response.data)
          return response.data
        })
        .catch((error) => Promise.reject(error || new Error('Failed to load attractions')))
    },
    loadBooking(bookingNumber, token) {
      this.bookingNumber = bookingNumber
      this.bookingToken = token
      return api
        .bookings.get(bookingNumber, token)
        .then((response) => {
          this.setBooking(response.data)
          return response
        })
        .catch((error) => Promise.reject(error.response || new Error('Failed to load booking')))
    },
    loadOptions() {
      if (this.options.gateway) {
        return Promise.resolve(this.options)
      }

      return api
        .options()
        .then((response) => {
          // A cut-off request resolves with an empty body, as in loadAttractions above.
          if (!isPlainObject(response?.data)) {
            return Promise.reject(new Error(`Unexpected options response (status ${response?.status})`))
          }

          this.setOptions(response.data)
          return response.data
        })
        .catch((error) => Promise.reject(error || new Error('Failed to load options')))
    },
    setRecapture(response) {
      this['g-recaptcha-response'] = response
    },
    setStatus(status) {
      this.status = status
    },
    setBooking(booking) {
      this.booking = booking
    },
    setBookingNumber(bookingNumber) {
      this.bookingNumber = bookingNumber
    },
    setBookingToken(bookingToken) {
      this.bookingToken = bookingToken
    },
    setQuote(quote) {
      this.quote = quote
    },
    setItems(items) {
      this.items = items
    },
    setConfirms(confirms) {
      this.confirms = normaliseConfirmationValues(confirms)
    },
    setItem(index, item) {
      this.items[index] = item
    },
    addItem(item) {
      this.items.push(item)
    },
    removeItem(item) {
      this.items = this.items.filter((current) => current.id !== item.id)
    },
    clearPrices() {
      this.items = this.items.map((current) => ({ ...current, prices: [], availability: {} }))
    },
    async preparePayment() {
      const args = {
        ...this.form,
        amount: (this.quote?.totalDue ?? 0) * 100,
      }

      const result = this.preparedPayment?.intent?.id
        ? await api.intents.update(this.preparedPayment.intent.id, args)
        : await api.intents.create(args)

      this.setPreparedPayment(result.data)

      return result.data
    },
    setPreparedPayment(preparedPayment) {
      this.preparedPayment = preparedPayment
    },
    setPayment(payment) {
      this.payment = payment
    },
    setAttractions(attractions) {
      this.attractions = attractions
    },
    setOptions(options) {
      this.options = options

      if (isEmpty(this.options.gateway)) {
        this.options.gateway = 'card'
      }
    },
    copyQuoteToBooking() {
      this.booking = this.quote
      this.booking.orderNumber = generateRandomString(6)
    },
    getAttractionByCode(code) {
      return this.attractions.find((attraction) => attraction.code === code) ?? null
    },
    getAttraction(id) {
      return this.attractions.find((attraction) => Number(attraction.id) === Number(id)) ?? null
    },
    getCartItemCutoffMinutes(item) {
      const attraction = this.getAttraction(item?.attraction)

      if (!attraction) {
        return 0
      }

      if (attraction?.cutoff_minutes !== null && attraction?.cutoff_minutes !== undefined && attraction?.cutoff_minutes !== '') {
        return Number(attraction.cutoff_minutes)
      }

      const option = Array.isArray(attraction?.rezdy_product_options)
        ? attraction.rezdy_product_options.find((current) => current.product === item?.product_code)
        : null

      return Number(option?.minimumNoticeMinutes ?? 0)
    },
    isCartItemPastCutoff(item) {
      if (!item?.time) {
        return false
      }

      const cutoffMinutes = this.getCartItemCutoffMinutes(item)
      return !moment(item.time).isAfter(moment().add(cutoffMinutes, 'minutes'))
    },
    pushCartMessage(message) {
      if (!message) {
        return
      }

      this.cartMessages = [...this.cartMessages, message]
    },
    isUpsellSelected(upsellId) {
      const resolvedUpsellId = Number(upsellId)

      return this.selectedUpsellOffers.some((offer) => resolveUpsellId(offer) === resolvedUpsellId)
    },
    selectUpsell(offer) {
      const upsellId = resolveUpsellId(offer)

      if (!upsellId) {
        return null
      }

      const nextOffer = {
        ...offer,
        id: upsellId,
      }

      this.selectedUpsellOffers = [
        ...this.selectedUpsellOffers.filter((currentOffer) => resolveUpsellId(currentOffer) !== upsellId),
        nextOffer,
      ]

      return nextOffer
    },
    removeSelectedUpsell(upsellId) {
      const resolvedUpsellId = Number(upsellId)

      this.selectedUpsellOffers = this.selectedUpsellOffers.filter((offer) => resolveUpsellId(offer) !== resolvedUpsellId)
    },
    syncSelectedUpsells(items = this.cartItems) {
      this.selectedUpsellOffers = this.selectedUpsellOffers.filter((offer) => upsellMatchesCart(offer, items))
    },
    clearSelectedUpsells() {
      this.selectedUpsellOffers = []
    },
    clearCartMessages() {
      this.cartMessages = []
    },
    setUpsells(placement, offers = []) {
      this.upsellsByPlacement = {
        ...this.upsellsByPlacement,
        [placement]: Array.isArray(offers) ? offers : [],
      }
    },
    setUpsellsLoading(placement, loading) {
      this.upsellsLoading = {
        ...this.upsellsLoading,
        [placement]: Boolean(loading),
      }
    },
    clearUpsells(placement = null) {
      if (placement) {
        this.setUpsells(placement, [])
        this.setUpsellsLoading(placement, false)
        return
      }

      this.upsellsByPlacement = {
        widget: [],
        cart: [],
      }
      this.upsellsLoading = {
        widget: false,
        cart: false,
      }
    },
    async loadUpsells(placement, items = this.cartItems) {
      if (!placement) {
        return []
      }

      this.syncSelectedUpsells(items)

      const requestItems = buildUpsellRequestItems(items)

      if (!requestItems.length) {
        this.setUpsells(placement, [])
        return []
      }

      this.setUpsellsLoading(placement, true)

      try {
        const response = await api.upsells({
          appear_in: placement,
          limit: 1,
          items: requestItems,
        })

        const offers = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : []

        this.setUpsells(placement, offers)
        offers.forEach((offer) => {
          if (this.isUpsellSelected(offer?.id)) {
            this.selectUpsell(offer)
          }
        })
        return offers
      } catch (error) {
        this.setUpsells(placement, [])
        return []
      } finally {
        this.setUpsellsLoading(placement, false)
      }
    },
    addToCart(payload) {
      if (this.findCartItemForSession(payload)) {
        return null
      }

      const reservationExpiresAt = this.reservationExpiry
      const item = {
        id: uuid(),
        extras: [],
        product_code: payload.product_code ?? '',
        reservationExpiresAt,
        ...payload,
        confirms: normaliseConfirmationValues(payload.confirms ?? payload.acceptedRequirements ?? []),
      }

      this.cartItems = [
        ...this.cartItems.map((current) => ({
          ...current,
          reservationExpiresAt,
        })),
        item,
      ]
      this.lastAddedItemId = item.id
      return item
    },
    removeFromCart(itemId) {
      this.cartItems = this.cartItems.filter((item) => item.id !== itemId)
      if (this.lastAddedItemId === itemId) {
        this.lastAddedItemId = null
      }

      this.items = this.items.filter((item) => item.id !== itemId)
      this.syncSelectedUpsells()
      this.syncCartConfirms()
    },
    restoreCartItem(item, index = this.cartItems.length) {
      if (
        !item?.id
        || this.cartItems.some((current) => current.id === item.id)
        || this.findCartItemForSession(item)
      ) {
        return null
      }

      const numericIndex = Number(index)
      const insertAt = Number.isFinite(numericIndex)
        ? Math.min(Math.max(numericIndex, 0), this.cartItems.length)
        : this.cartItems.length
      const nextItems = [...this.cartItems]
      nextItems.splice(insertAt, 0, item)

      this.cartItems = nextItems
      this.items = this.buildBookingItems()
      this.syncSelectedUpsells()
      this.syncCartConfirms()

      return item
    },
    updateCartItem(itemId, payload = {}) {
      const existing = this.cartItems.find((item) => item.id === itemId)

      if (!existing) {
        return null
      }

      if (this.findCartItemForSession(payload, itemId)) {
        return null
      }

      const updated = {
        ...existing,
        ...payload,
        id: existing.id,
        extras: payload.extras ?? existing.extras ?? [],
        product_code: payload.product_code ?? existing.product_code ?? '',
        confirms: normaliseConfirmationValues(payload.confirms ?? payload.acceptedRequirements ?? existing.confirms ?? existing.acceptedRequirements ?? []),
      }

      this.cartItems = this.cartItems.map((item) => item.id === itemId ? updated : item)
      this.items = this.buildBookingItems()
      this.quote = {}
      this.syncSelectedUpsells()
      this.syncCartConfirms()

      return updated
    },
    updateParticipants(itemId, participants) {
      this.cartItems = this.cartItems.map((item) => item.id === itemId ? { ...item, participants: { ...participants } } : item)
      this.quote = {}
    },
    adjustParticipant(itemId, key, amount) {
      this.cartItems = this.cartItems.map((item) => {

        if (item.id !== itemId) {
          return item
        }

        const participantKey = normaliseParticipantKey(key)
        const current = Number(item.participants?.[participantKey] ?? 0)
        const next = Math.max(0, current + amount)

        const updatedParticipants = {
          ...item.participants,
          [participantKey]: next,
        }

        const seatsAvailable = Number(item?.availability?.seatsAvailable ?? 0)
        if (amount > 0 && seatsAvailable > 0 && getSeatsUsed(item, updatedParticipants) > seatsAvailable) {
          return item
        }

        return {
          ...item,
          participants: updatedParticipants,
        }
      })
      this.quote = {}
    },
    clearCart() {
      this.cartItems = []
      this.lastAddedItemId = null
      this.items = []
      this.quote = {}
      this.setConfirms([])
      this.clearSelectedUpsells()
      this.clearUpsells()
    },
    findCartItemForSession(candidate, excludeItemId = null) {
      const sessionKey = cartItemSessionKey(candidate)

      if (!sessionKey) {
        return null
      }

      return this.cartItems.find((item) => (
        item.id !== excludeItemId && cartItemSessionKey(item) === sessionKey
      )) ?? null
    },
    deduplicateCartSessions() {
      const seenSessions = new Set()
      const duplicateIds = new Set()

      this.cartItems.forEach((item) => {
        const sessionKey = cartItemSessionKey(item)

        if (!sessionKey || !seenSessions.has(sessionKey)) {
          if (sessionKey) {
            seenSessions.add(sessionKey)
          }
          return
        }

        duplicateIds.add(item.id)
      })

      if (!duplicateIds.size) {
        return 0
      }

      this.cartItems = this.cartItems.filter((item) => !duplicateIds.has(item.id))
      this.items = this.buildBookingItems()
      this.quote = {}
      this.syncSelectedUpsells()
      this.syncCartConfirms()

      if (this.lastAddedItemId && duplicateIds.has(this.lastAddedItemId)) {
        this.lastAddedItemId = null
      }

      this.pushCartMessage(
        duplicateIds.size === 1
          ? `${duplicateSessionCartMessage} A duplicate item was removed.`
          : `${duplicateSessionCartMessage} ${duplicateIds.size} duplicate items were removed.`
      )

      return duplicateIds.size
    },
    setCustomer(payload) {
      this.customer = {
        ...this.customer,
        ...payload,
      }
    },
    buildBookingItems() {
      return this.cartItems.map((item) => ({
        id: item.id,
        attraction: item.attraction,
        product_code: item.product_code ?? '',
        prices: item.prices ?? item.availability?.priceOptions ?? [],
        availability: item.availability ?? {},
        extras: item.extras ?? [],
        time: item.time,
        participants: item.participants,
        confirms: normaliseConfirmationValues(item.confirms ?? item.acceptedRequirements ?? []),
      }))
    },
    buildCartConfirms() {
      return normaliseConfirmationValues(this.cartItems.flatMap((item) => item.confirms ?? item.acceptedRequirements ?? []))
    },
    syncCartConfirms() {
      this.setConfirms(this.buildCartConfirms())
    },
    syncCartToItems() {
      this.deduplicateCartSessions()
      this.setItems(this.buildBookingItems())
      this.syncCartConfirms()
      this.syncSelectedUpsells(this.items)
    },
    refreshCartReservations() {
      if (!this.cartItems.length) {
        return
      }

      const reservationExpiresAt = this.reservationExpiry
      this.cartItems = this.cartItems.map((item) => ({
        ...item,
        reservationExpiresAt,
      }))
    },
    pruneExpiredCart() {
      if (!this.cartItems.length) {
        return false
      }

      const now = Date.now()
      const hasExpiredItems = this.cartItems.some((item) => Number(item.reservationExpiresAt ?? 0) <= now)

      if (!hasExpiredItems) {
        return false
      }

      this.clearCart()
      return true
    },
    pruneUnavailableCart() {
      if (!this.cartItems.length) {
        return false
      }

      const removedItems = this.cartItems.filter((item) => this.isCartItemPastCutoff(item))

      if (!removedItems.length) {
        return false
      }

      const removedIds = new Set(removedItems.map((item) => item.id))

      this.cartItems = this.cartItems.filter((item) => !removedIds.has(item.id))
      this.items = this.items.filter((item) => !removedIds.has(item.id))
      this.syncSelectedUpsells()
      this.syncCartConfirms()

      if (this.lastAddedItemId && removedIds.has(this.lastAddedItemId)) {
        this.lastAddedItemId = null
      }

      removedItems.forEach(() => {
        this.pushCartMessage('This item has been removed from the cart because it is no longer available within the online booking cutoff.')
      })

      if (!this.cartItems.length) {
        this.quote = {}
      }

      return true
    },
  },
  persist: [
    {
      pick: ['customer', 'cartItems', 'lastAddedItemId', 'selectedUpsellOffers'],
      storage: localStorage,
    },
    {
      pick: ['items', 'cartMessages'],
      storage: sessionStorage,
      beforeHydrate: (ctx) => {
        ctx.store.clearPrices()
      },
    },
  ],
})

export default useCheckoutCartStore
